import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { API, getToken } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Plus, Trash2, Save, FileSignature, ArrowLeft, LayoutTemplate,
  BookmarkPlus, Download, Eye, EyeOff, GripVertical, BookOpen, X, Search,
  AlertTriangle, UploadCloud, Loader2, Ruler, ShieldCheck, Lock, FileMinus,
} from "lucide-react";

const euro = (n) => (n || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
const TYPE_LABEL = { devis: "Devis", facture: "Facture", bon_commande: "Bon de commande", bon_livraison: "Bon de livraison" };
const UNITS = ["u", "m", "m²", "ml", "h", "forfait", "lot", "kg"];
const emptyItem = () => ({ description: "", quantity: 1, unit: "u", unit_price: 0, cost_price: 0, discount: 0, vat_rate: 20, section: "" });
const today = () => new Date().toISOString().slice(0, 10);

const lineHT = (it) => (it.quantity || 0) * (it.unit_price || 0) * (1 - (it.discount || 0) / 100);
const lineCost = (it) => (it.quantity || 0) * (it.cost_price || 0);
const lineMargin = (it) => lineHT(it) - lineCost(it);
const linePct = (it) => (lineHT(it) ? (lineMargin(it) / lineHT(it)) * 100 : 0);

export default function DocumentEditor() {
  const { id, type } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isNew = !id;
  const [doc, setDoc] = useState({
    type: type || "devis", client_id: "", client_name: "", title: "", intro: "",
    date: today(), due_date: "", notes: "", terms: "", status: "brouillon",
    items: [emptyItem()], annexes: [], from_study: "",
  });
  const [clients, setClients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showTpl, setShowTpl] = useState(false);
  const [signed, setSigned] = useState(false);
  const [locked, setLocked] = useState(false);
  const [number, setNumber] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [showCatalog, setShowCatalog] = useState(false);
  const [catQuery, setCatQuery] = useState("");
  const [minMargin, setMinMargin] = useState(0);
  const [uploadingIdx, setUploadingIdx] = useState(-1);
  const [originStudy, setOriginStudy] = useState(null);

  useEffect(() => {
    api.get("/templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/catalog").then((r) => setCatalog(r.data)).catch(() => {});
    api.get("/settings").then((r) => setMinMargin(r.data.min_margin_pct || 0)).catch(() => {});
    if (!isNew) {
      api.get(`/documents/${id}`).then((r) => {
        const d = r.data;
        setDoc({
          type: d.type, client_id: d.client_id || "", client_name: d.client_name || "", title: d.title || "", intro: d.intro || "",
          date: (d.date || today()).slice(0, 10), due_date: (d.due_date || "").slice(0, 10),
          notes: d.notes || "", terms: d.terms || "", status: d.status, annexes: d.annexes || [], from_study: d.from_study || "",
          items: d.items?.length ? d.items.map((i) => ({ discount: 0, section: "", cost_price: 0, ...i })) : [emptyItem()],
        });
        setSigned(d.signed);
        setLocked(!!d.locked);
        setNumber(d.number);
        if (d.from_study) api.get(`/studies/${d.from_study}`).then((rs) => setOriginStudy(rs.data)).catch(() => {});
      });
    }
  }, [id]);

  // totals + VAT breakdown
  const totalHT = doc.items.reduce((s, it) => s + lineHT(it), 0);
  const vatMap = {};
  doc.items.forEach((it) => {
    const base = lineHT(it);
    vatMap[it.vat_rate] = (vatMap[it.vat_rate] || 0) + base * (it.vat_rate || 0) / 100;
  });
  const totalTVA = Object.values(vatMap).reduce((s, v) => s + v, 0);
  const totalTTC = totalHT + totalTVA;
  const totalCost = doc.items.reduce((s, it) => s + lineCost(it), 0);
  const totalMargin = totalHT - totalCost;
  const originMeasures = [];
  if (originStudy) (originStudy.photos || []).forEach((p) => {
    const px = p.calibration?.pxPerCm; if (!px) return;
    (p.annotations || []).forEach((a) => {
      if (a.type === "rect") { const w = Math.round(Math.abs(a.x2 - a.x1) / px), h = Math.round(Math.abs(a.y2 - a.y1) / px); if (w && h) originMeasures.push(`${w}×${h} cm`); }
      else if (a.type === "dimension") { const L = Math.round(Math.hypot(a.x2 - a.x1, a.y2 - a.y1) / px); if (L) originMeasures.push(`${L} cm`); }
    });
  });
  const totalMarginPct = totalHT ? (totalMargin / totalHT) * 100 : 0;

  const setItem = (i, k, v) => {
    const items = [...doc.items];
    items[i] = { ...items[i], [k]: (k === "description" || k === "unit" || k === "section") ? v : parseFloat(v) || 0 };
    setDoc({ ...doc, items });
  };
  const onClientSelect = (val) => {
    if (val === "__free__") return setDoc({ ...doc, client_id: "", client_name: "" });
    const c = clients.find((x) => x.id === val);
    setDoc({ ...doc, client_id: val, client_name: c ? c.name : "" });
  };
  const saveMinMargin = async (v) => {
    const val = parseFloat(v) || 0;
    setMinMargin(val);
    try { await api.put("/settings", { min_margin_pct: val }); } catch {}
  };
  const annexeSrc = (url) => (url && url.includes("/api/files/")) ? `${url}${url.includes("?") ? "&" : "?"}auth=${getToken()}` : url;
  const uploadAnnexe = async (i, fileList) => {
    const file = fileList && fileList[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Seules les images sont acceptées");
    setUploadingIdx(i);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const an = [...(doc.annexes || [])];
      an[i] = { ...an[i], url: `${API}${data.url.slice(4)}`, caption: an[i]?.caption || file.name.replace(/\.[^.]+$/, "") };
      setDoc({ ...doc, annexes: an });
      toast.success("Photo ajoutée");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'upload");
    } finally {
      setUploadingIdx(-1);
    }
  };
  const addFromCatalog = (c) => {
    const item = { description: c.name, quantity: 1, unit: c.unit || "u", unit_price: c.unit_price || 0,
      cost_price: c.cost_price || 0, discount: 0, vat_rate: c.vat_rate ?? 20, section: c.kind === "service" ? "Main d'œuvre" : "Produits" };
    const first = doc.items.length === 1 && !doc.items[0].description;
    setDoc({ ...doc, items: first ? [item] : [...doc.items, item] });
    toast.success(`« ${c.name} » ajouté`);
  };

  const save = async () => {
    const payload = { ...doc };
    if (isNew) {
      const { data } = await api.post("/documents", payload);
      toast.success(`${TYPE_LABEL[doc.type]} créé`);
      navigate(`/documents/${data.id}`, { replace: true });
    } else {
      await api.put(`/documents/${id}`, payload);
      toast.success("Enregistré");
    }
  };
  const sign = async () => { await api.post(`/documents/${id}/sign`); setSigned(true); toast.success("Document signé électroniquement"); };
  const validate = async () => {
    if (!window.confirm("Valider ce document ? Il recevra un numéro définitif et sera verrouillé (non modifiable). Les corrections se feront ensuite par avoir.")) return;
    try {
      const { data } = await api.post(`/documents/${id}/validate`);
      setLocked(true); setNumber(data.number); setDoc((d) => ({ ...d, status: data.status }));
      toast.success("Facture validée et verrouillée");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur de validation"); }
  };
  const creditNote = async () => {
    try {
      const { data } = await api.post(`/documents/${id}/credit-note`);
      toast.success(`Avoir ${data.number} créé`);
      navigate(`/documents/${data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const downloadPdf = async () => {
    try {
      const res = await api.get(`/documents/${id}/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) { toast.error("Impossible de générer le PDF"); }
  };
  const applyTemplate = (t) => {
    setDoc({ ...doc, title: t.title || doc.title, notes: t.notes || doc.notes,
      items: t.items?.length ? t.items.map((i) => ({ discount: 0, ...i })) : doc.items });
    setShowTpl(false);
    toast.success(`Template « ${t.name} » appliqué`);
  };
  const saveAsTemplate = async () => {
    const name = window.prompt("Nom du template ?", doc.title || "Mon template");
    if (!name) return;
    await api.post("/templates", { name, title: doc.title, items: doc.items, notes: doc.notes });
    api.get("/templates").then((r) => setTemplates(r.data));
    toast.success("Template enregistré");
  };

  const dueLabel = doc.type === "facture" ? "Échéance" : "Validité";

  const Preview = () => (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden text-[13px]" data-testid="doc-preview">
      <div className="bg-navy text-white px-6 py-5 flex items-center justify-between">
        <div className="font-display font-bold text-xl">{user?.company_name || "Fabriko"}</div>
        <div className="font-display font-bold text-lg text-sky-400 uppercase">{TYPE_LABEL[doc.type]}</div>
      </div>
      <div className="p-6">
        <div className="flex justify-between text-slate-600">
          <div>
            <div>N° <span className="font-mono">{number || "—"}</span></div>
            <div>Date : {doc.date}</div>
            {doc.due_date && <div>{dueLabel} : {doc.due_date}</div>}
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-wider text-slate-400">Client</div>
            <div className="font-semibold text-navy">{doc.client_name || "—"}</div>
          </div>
        </div>
        {doc.title && <div className="mt-3 text-navy font-medium">Objet : {doc.title}</div>}
        {doc.intro && <p className="mt-2 text-slate-600 text-xs leading-relaxed whitespace-pre-wrap">{doc.intro}</p>}
        <table className="w-full mt-5">
          <thead>
            <tr className="bg-slate-100 text-slate-500 text-[11px]">
              <th className="text-left px-2 py-1.5 font-medium">Désignation</th>
              <th className="text-center px-1 py-1.5 font-medium">Qté</th>
              <th className="text-right px-1 py-1.5 font-medium">P.U.</th>
              <th className="text-right px-1 py-1.5 font-medium">Rem.</th>
              <th className="text-right px-2 py-1.5 font-medium">Total HT</th>
            </tr>
          </thead>
          <tbody>
            {doc.items.flatMap((it, i) => {
              const prev = i > 0 ? doc.items[i - 1].section : null;
              const rows = [];
              if (it.section && it.section !== prev) {
                rows.push(<tr key={`s${i}`}><td colSpan={5} className="px-2 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wider text-sky-600">{it.section}</td></tr>);
              }
              rows.push(
                <tr key={i} className="border-b border-slate-50">
                  <td className="px-2 py-1.5 text-navy">{it.description || <span className="text-slate-300">…</span>}</td>
                  <td className="text-center px-1 py-1.5 font-mono">{it.quantity}{it.unit ? ` ${it.unit}` : ""}</td>
                  <td className="text-right px-1 py-1.5 font-mono">{euro(it.unit_price)}</td>
                  <td className="text-right px-1 py-1.5 font-mono">{it.discount ? `${it.discount}%` : "—"}</td>
                  <td className="text-right px-2 py-1.5 font-mono font-medium">{euro(lineHT(it))}</td>
                </tr>
              );
              return rows;
            })}
          </tbody>
        </table>
        <div className="mt-4 flex justify-end">
          <div className="w-56 space-y-1">
            <div className="flex justify-between text-slate-600"><span>Total HT</span><span className="font-mono">{euro(totalHT)}</span></div>
            {Object.entries(vatMap).map(([r, v]) => (
              <div key={r} className="flex justify-between text-slate-500 text-xs"><span>TVA {r}%</span><span className="font-mono">{euro(v)}</span></div>
            ))}
            <div className="flex justify-between font-bold text-navy pt-1 border-t border-slate-200"><span>Total TTC</span><span className="font-mono">{euro(totalTTC)}</span></div>
          </div>
        </div>
        {(doc.annexes || []).filter((a) => a.url).length > 0 && (
          <div className="mt-4">
            <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">Annexes</div>
            <div className="grid grid-cols-3 gap-2">
              {doc.annexes.filter((a) => a.url).map((a, i) => (
                <figure key={i}><img src={annexeSrc(a.url)} alt={a.caption} className="w-full h-20 object-cover rounded-lg" /><figcaption className="text-[10px] text-slate-500 mt-0.5 truncate">{a.caption}</figcaption></figure>
              ))}
            </div>
          </div>
        )}
        {doc.notes && <p className="mt-4 text-xs text-slate-500">Notes : {doc.notes}</p>}
        {doc.terms && <p className="mt-1 text-xs text-slate-400">{doc.terms}</p>}
        {signed && <p className="mt-3 text-xs text-emerald-600 font-semibold">✓ Signé électroniquement</p>}
      </div>
    </div>
  );

  return (
    <div className="space-y-5 animate-fade-up">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button onClick={() => navigate("/documents")} className="inline-flex items-center gap-2 text-slate-500 hover:text-navy text-sm" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setShowPreview((v) => !v)} data-testid="toggle-preview-btn"
            className="xl:hidden inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-slate-200 text-sm font-medium hover:border-sky-300">
            {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />} Aperçu
          </button>
          <button onClick={() => setShowTpl(!showTpl)} data-testid="template-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-slate-200 text-sm font-medium hover:border-sky-300">
            <LayoutTemplate className="w-4 h-4" /> Templates
          </button>
          {!isNew && (
            <button onClick={downloadPdf} data-testid="pdf-btn" className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-slate-200 text-sm font-medium hover:border-sky-300">
              <Download className="w-4 h-4" /> PDF
            </button>
          )}
          {!isNew && !locked && doc.type === "devis" && (
            <button onClick={sign} data-testid="sign-btn" className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-600">
              <FileSignature className="w-4 h-4" /> Signer
            </button>
          )}
          {!isNew && !locked && doc.type === "facture" && (
            <button onClick={validate} data-testid="validate-doc-btn" className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600">
              <ShieldCheck className="w-4 h-4" /> Valider la facture
            </button>
          )}
          {!isNew && locked && doc.type === "facture" && !doc.is_credit_note && (
            <button onClick={creditNote} data-testid="credit-note-btn" className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-slate-200 text-rose-600 text-sm font-semibold hover:border-rose-300">
              <FileMinus className="w-4 h-4" /> Créer un avoir
            </button>
          )}
          {!locked && (
            <button onClick={save} data-testid="save-doc-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-navy text-white text-sm font-semibold hover:bg-navy-deep">
              <Save className="w-4 h-4" /> Enregistrer
            </button>
          )}
        </div>
      </div>

      {showTpl && (
        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-navy text-sm">Partir d'un template</h3>
            <button onClick={saveAsTemplate} className="inline-flex items-center gap-1.5 text-xs text-sky-600 font-semibold" data-testid="save-template-btn">
              <BookmarkPlus className="w-4 h-4" /> Enregistrer comme template
            </button>
          </div>
          <div className="grid sm:grid-cols-3 gap-2">
            {templates.map((t) => (
              <button key={t.id} onClick={() => applyTemplate(t)} data-testid={`tpl-${t.id}`}
                className="text-left p-3 rounded-xl border border-slate-200 hover:border-sky-400 hover:bg-sky-50/40 transition-colors">
                <div className="text-sm font-semibold text-navy">{t.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">{t.items?.length || 0} lignes</div>
              </button>
            ))}
            {templates.length === 0 && <p className="text-sm text-slate-400">Aucun template.</p>}
          </div>
        </div>
      )}

      <div className="grid xl:grid-cols-2 gap-5 items-start">
        {/* FORM */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h1 className="text-xl font-display font-bold text-navy">{TYPE_LABEL[doc.type]} {number && <span className="font-mono text-sm text-slate-400">· {number}</span>}</h1>
            <select value={doc.status} onChange={(e) => setDoc({ ...doc, status: e.target.value })} data-testid="doc-status" disabled={locked}
              className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sky-400 disabled:bg-slate-100 disabled:text-slate-500">
              <option value="brouillon">Brouillon</option>
              <option value="envoye">Envoyé</option>
              <option value="signe">Signé</option>
              <option value="emise">Émise</option>
              <option value="valide">Validé</option>
              <option value="paye">Payé</option>
              <option value="refuse">Refusé</option>
            </select>
          </div>

          {locked && (
            <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2.5 flex items-center gap-2" data-testid="locked-banner">
              <Lock className="w-4 h-4 text-amber-600 shrink-0" />
              <span className="text-xs text-amber-800 font-medium">
                {doc.is_credit_note ? "Avoir verrouillé" : "Facture validée et verrouillée"} — non modifiable (conformité facturation). Corrigez via un avoir.
              </span>
            </div>
          )}

          {doc.from_study && (
        <div className="mt-3 rounded-xl bg-sky-50 border border-sky-100 px-3 py-2 flex items-center justify-between gap-2 flex-wrap" data-testid="origin-study-banner">
          <div className="text-xs text-slate-600 flex items-center gap-1.5 min-w-0">
            <Ruler className="w-3.5 h-3.5 text-sky-500 shrink-0" />
            <span className="truncate">Issu de l'étude photo{originStudy ? ` « ${originStudy.title} »` : ""}{originMeasures.length > 0 && <span className="text-slate-400"> · {originMeasures.slice(0, 6).join(" · ")}</span>}</span>
          </div>
          <Link to={`/etudes/${doc.from_study}`} data-testid="origin-study-link" className="text-xs font-semibold text-sky-600 hover:underline whitespace-nowrap">Ouvrir l'étude →</Link>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3 mt-4">
            <div>
              <label className="text-xs font-medium text-slate-500">Client</label>
              <select value={doc.client_id || "__free__"} onChange={(e) => onClientSelect(e.target.value)} data-testid="doc-client-select"
                className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-400">
                <option value="__free__">— Saisie libre —</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}{c.city ? ` (${c.city})` : ""}</option>)}
              </select>
              {!doc.client_id && (
                <input value={doc.client_name} onChange={(e) => setDoc({ ...doc, client_name: e.target.value })} data-testid="doc-client-input" placeholder="Nom du client"
                  className="mt-2 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" />
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Objet</label>
              <input value={doc.title} onChange={(e) => setDoc({ ...doc, title: e.target.value })} data-testid="doc-title-input"
                className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Date</label>
              <input type="date" value={doc.date} onChange={(e) => setDoc({ ...doc, date: e.target.value })} data-testid="doc-date"
                className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">{dueLabel}</label>
              <input type="date" value={doc.due_date} onChange={(e) => setDoc({ ...doc, due_date: e.target.value })} data-testid="doc-due-date"
                className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" />
            </div>
          </div>

          {doc.type === "devis" && (
            <div className="mt-5">
              <label className="text-xs font-medium text-slate-500">Texte de présentation (proposition commerciale)</label>
              <textarea value={doc.intro} onChange={(e) => setDoc({ ...doc, intro: e.target.value })} data-testid="doc-intro-input" rows={2} placeholder="Introduction / présentation de votre offre au client…"
                className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" />
            </div>
          )}
          {/* line items */}
          <div className="mt-5">
            <div className="hidden sm:grid grid-cols-12 gap-2 text-[11px] font-medium text-slate-500 px-1 mb-1">
              <div className="col-span-4">Désignation</div>
              <div className="col-span-2 text-center">Qté / Unité</div>
              <div className="col-span-2 text-right">P.U. HT</div>
              <div className="col-span-1 text-right">Rem%</div>
              <div className="col-span-2 text-right">TVA%</div>
              <div className="col-span-1"></div>
            </div>
            {doc.items.map((it, i) => (
              <div key={i} className="mb-2" data-testid={`item-row-${i}`}>
                <input value={it.section || ""} onChange={(e) => setItem(i, "section", e.target.value)} placeholder="Sous-catégorie (ex : Main d'œuvre, Produits) — optionnel" data-testid={`item-section-${i}`}
                  className="w-full mb-1 px-2 py-1 rounded-md bg-slate-50 border border-dashed border-slate-200 text-xs text-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-400" />
                <div className="grid grid-cols-12 gap-2 items-center">
                <input value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} placeholder="Désignation" data-testid={`item-desc-${i}`}
                  className="col-span-12 sm:col-span-4 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" />
                <div className="col-span-6 sm:col-span-2 flex gap-1">
                  <input type="number" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} data-testid={`item-qty-${i}`}
                    className="w-1/2 px-2 py-2 rounded-lg border border-slate-200 text-sm text-right font-mono focus:outline-none focus:ring-2 focus:ring-sky-400" />
                  <select value={it.unit} onChange={(e) => setItem(i, "unit", e.target.value)} data-testid={`item-unit-${i}`}
                    className="w-1/2 px-1 py-2 rounded-lg border border-slate-200 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-sky-400">
                    {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <input type="number" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} data-testid={`item-price-${i}`}
                  className="col-span-3 sm:col-span-2 px-2 py-2 rounded-lg border border-slate-200 text-sm text-right font-mono focus:outline-none focus:ring-2 focus:ring-sky-400" />
                <input type="number" value={it.discount} onChange={(e) => setItem(i, "discount", e.target.value)} data-testid={`item-discount-${i}`}
                  className="col-span-3 sm:col-span-1 px-2 py-2 rounded-lg border border-slate-200 text-sm text-right font-mono focus:outline-none focus:ring-2 focus:ring-sky-400" />
                <select value={it.vat_rate} onChange={(e) => setItem(i, "vat_rate", e.target.value)} data-testid={`item-vat-${i}`}
                  className="col-span-4 sm:col-span-2 px-2 py-2 rounded-lg border border-slate-200 text-sm text-right bg-white focus:outline-none focus:ring-2 focus:ring-sky-400">
                  {[0, 5.5, 10, 20].map((r) => <option key={r} value={r}>{r}%</option>)}
                </select>
                <button onClick={() => setDoc({ ...doc, items: doc.items.filter((_, x) => x !== i) })} data-testid={`item-remove-${i}`}
                  className="col-span-2 sm:col-span-1 p-2 rounded-lg text-rose-500 hover:bg-rose-50 justify-self-end"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 px-1 text-[11px] text-slate-500">
                  <label className="inline-flex items-center gap-1.5">
                    <span className="text-slate-400">Coût d'achat HT</span>
                    <input type="number" value={it.cost_price} onChange={(e) => setItem(i, "cost_price", e.target.value)} data-testid={`item-cost-${i}`}
                      className="w-24 px-2 py-1 rounded-md border border-slate-200 text-right font-mono focus:outline-none focus:ring-2 focus:ring-sky-400" />
                    <span className="text-slate-300">/ {it.unit}</span>
                  </label>
                  <span className="inline-flex items-center gap-1 flex-wrap" data-testid={`item-margin-${i}`}>
                    <span className="text-slate-400">Marge</span>
                    {(() => { const low = minMargin > 0 && lineHT(it) > 0 && linePct(it) < minMargin; return (<>
                      <span className={`font-mono font-semibold ${low ? "text-rose-600" : lineMargin(it) >= 0 ? "text-emerald-600" : "text-rose-500"}`}>
                        {euro(lineMargin(it))} ({lineHT(it) ? linePct(it).toFixed(0) : 0}%)
                      </span>
                      {low && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-600 text-[10px] font-semibold" data-testid={`item-margin-alert-${i}`}><AlertTriangle className="w-3 h-3" /> sous le seuil</span>}
                    </>); })()}
                  </span>
                </div>
              </div>
            ))}
            <button onClick={() => setDoc({ ...doc, items: [...doc.items, emptyItem()] })} data-testid="add-item-btn"
              className="mt-1 inline-flex items-center gap-2 text-sm text-sky-600 font-semibold hover:underline">
              <Plus className="w-4 h-4" /> Ajouter une ligne
            </button>
            <button onClick={() => { setShowCatalog(true); setCatQuery(""); }} data-testid="import-catalog-btn"
              className="mt-1 ml-4 inline-flex items-center gap-2 text-sm text-navy font-semibold hover:underline">
              <BookOpen className="w-4 h-4" /> Importer du catalogue
            </button>
          </div>

          <div className="mt-5 flex justify-end">
            <div className="w-full sm:w-64 space-y-1.5">
              <div className="flex justify-between text-sm text-slate-600"><span>Total HT</span><span className="font-mono tabular">{euro(totalHT)}</span></div>
              {Object.entries(vatMap).map(([r, v]) => (
                <div key={r} className="flex justify-between text-xs text-slate-500"><span>TVA {r}%</span><span className="font-mono tabular">{euro(v)}</span></div>
              ))}
              <div className="flex justify-between text-lg font-bold text-navy pt-2 border-t border-slate-200"><span>Total TTC</span><span className="font-mono tabular" data-testid="doc-total-ttc">{euro(totalTTC)}</span></div>
            </div>
          </div>

          <div className="mt-4 rounded-xl border border-dashed border-amber-300 bg-amber-50/50 p-3" data-testid="margin-recap">
            <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-amber-600">
                <Eye className="w-3.5 h-3.5" /> Marge (interne — non visible par le client)
              </div>
              <label className="flex items-center gap-1.5 text-[11px] text-slate-500">
                Seuil d'alerte
                <input type="number" value={minMargin} onChange={(e) => setMinMargin(parseFloat(e.target.value) || 0)} onBlur={(e) => saveMinMargin(e.target.value)} data-testid="min-margin-input"
                  className="w-16 px-2 py-1 rounded-md border border-slate-200 text-right font-mono text-xs focus:outline-none focus:ring-2 focus:ring-sky-400" />
                <span>%</span>
              </label>
            </div>
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div><div className="text-xs text-slate-500">Coût d'achat</div><div className="font-mono font-semibold text-slate-700" data-testid="recap-cost">{euro(totalCost)}</div></div>
              <div><div className="text-xs text-slate-500">Marge brute</div><div className={`font-mono font-semibold ${totalMargin >= 0 ? "text-emerald-600" : "text-rose-500"}`} data-testid="recap-margin">{euro(totalMargin)}</div></div>
              <div>
                <div className="text-xs text-slate-500">Taux de marge</div>
                <div className={`font-mono font-semibold inline-flex items-center gap-1 ${minMargin > 0 && totalMarginPct < minMargin ? "text-rose-600" : totalMargin >= 0 ? "text-emerald-600" : "text-rose-500"}`} data-testid="recap-margin-pct">
                  {totalMarginPct.toFixed(1)}%
                  {minMargin > 0 && totalMarginPct < minMargin && <AlertTriangle className="w-3.5 h-3.5" data-testid="recap-margin-alert" />}
                </div>
              </div>
            </div>
            {minMargin > 0 && totalMarginPct < minMargin && (
              <p className="mt-2 text-[11px] text-rose-600 flex items-center gap-1" data-testid="margin-global-alert">
                <AlertTriangle className="w-3.5 h-3.5" /> Marge globale sous votre seuil de {minMargin}%.
              </p>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-3 mt-5">
            <div>
              <label className="text-xs font-medium text-slate-500">Notes</label>
              <textarea value={doc.notes} onChange={(e) => setDoc({ ...doc, notes: e.target.value })} data-testid="doc-notes-input" rows={2}
                className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Conditions / mentions</label>
              <textarea value={doc.terms} onChange={(e) => setDoc({ ...doc, terms: e.target.value })} data-testid="doc-terms-input" rows={2} placeholder="Ex : Acompte 30% à la commande, TVA non applicable art. 293B…"
                className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" />
            </div>
          </div>

          {doc.type !== "bon_livraison" && (
            <div className="mt-5">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-medium text-slate-500">Annexes — photos, plans, dessins</label>
                <button onClick={() => setDoc({ ...doc, annexes: [...(doc.annexes || []), { url: "", caption: "" }] })} data-testid="add-annexe-btn" className="inline-flex items-center gap-1 text-xs text-sky-600 font-semibold"><Plus className="w-3.5 h-3.5" /> Ajouter</button>
              </div>
              <div className="space-y-2">
                {(doc.annexes || []).map((a, i) => (
                  <div key={i} className="flex gap-3 items-start rounded-xl border border-slate-200 p-2" data-testid={`annexe-${i}`}>
                    <label onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); uploadAnnexe(i, e.dataTransfer.files); }} data-testid={`annexe-drop-${i}`}
                      className="relative w-24 h-24 shrink-0 rounded-lg border-2 border-dashed border-slate-200 hover:border-sky-400 bg-slate-50 flex items-center justify-center cursor-pointer overflow-hidden transition-colors">
                      {a.url ? (
                        <img src={annexeSrc(a.url)} alt={a.caption} className="w-full h-full object-cover" />
                      ) : uploadingIdx === i ? (
                        <Loader2 className="w-5 h-5 text-sky-500 animate-spin" />
                      ) : (
                        <div className="text-center text-slate-400 px-1"><UploadCloud className="w-5 h-5 mx-auto" /><span className="text-[9px] leading-tight block mt-0.5">Glisser ou cliquer</span></div>
                      )}
                      {uploadingIdx === i && a.url && <div className="absolute inset-0 bg-white/60 flex items-center justify-center"><Loader2 className="w-5 h-5 text-sky-500 animate-spin" /></div>}
                      <input type="file" accept="image/*" className="hidden" data-testid={`annexe-file-${i}`} onChange={(e) => uploadAnnexe(i, e.target.files)} />
                    </label>
                    <div className="flex-1 min-w-0">
                      <input value={a.caption} onChange={(e) => { const an = [...doc.annexes]; an[i] = { ...an[i], caption: e.target.value }; setDoc({ ...doc, annexes: an }); }} placeholder="Légende (ex : plan de coupe)" data-testid={`annexe-caption-${i}`}
                        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" />
                      <p className="text-[11px] text-slate-400 mt-1">JPG, PNG, WebP — max 8 Mo</p>
                    </div>
                    <button onClick={() => setDoc({ ...doc, annexes: doc.annexes.filter((_, x) => x !== i) })} data-testid={`annexe-remove-${i}`} className="p-2 rounded-lg text-rose-500 hover:bg-rose-50"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* LIVE PREVIEW */}
        <div className={`${showPreview ? "block" : "hidden"} xl:block xl:sticky xl:top-20`}>
          <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Eye className="w-4 h-4" /> Aperçu en direct</div>
          <Preview />
        </div>
      </div>

      {showCatalog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setShowCatalog(false)} data-testid="catalog-import-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl flex flex-col max-h-[80vh]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h2 className="text-lg font-display font-bold text-navy">Importer du catalogue</h2>
              <button onClick={() => setShowCatalog(false)} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-5 pt-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input value={catQuery} onChange={(e) => setCatQuery(e.target.value)} data-testid="catalog-import-search" autoFocus
                  placeholder="Rechercher un produit ou service…"
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400" />
              </div>
            </div>
            <div className="p-5 pt-3 overflow-y-auto space-y-1.5">
              {catalog.filter((c) => !catQuery || c.name.toLowerCase().includes(catQuery.toLowerCase()) || (c.reference || "").toLowerCase().includes(catQuery.toLowerCase())).map((c) => (
                <button key={c.id} onClick={() => addFromCatalog(c)} data-testid={`catalog-pick-${c.id}`}
                  className="w-full text-left px-3 py-2.5 rounded-xl border border-slate-200 hover:border-sky-400 hover:bg-sky-50/40 transition-colors flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-navy truncate">{c.name}</div>
                    <div className="text-xs text-slate-400">{c.kind === "service" ? "Service" : "Produit"}{c.reference ? ` · ${c.reference}` : ""} · marge {c.unit_price ? (((c.unit_price - c.cost_price) / c.unit_price) * 100).toFixed(0) : 0}%</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-mono font-semibold text-navy">{euro(c.unit_price)}</div>
                    <div className="text-[11px] text-slate-400">/ {c.unit}</div>
                  </div>
                </button>
              ))}
              {catalog.length === 0 && (
                <div className="text-center py-8 text-sm text-slate-400">
                  Votre catalogue est vide.<br />Ajoutez des articles depuis la page <span className="font-semibold text-navy">Catalogue</span>.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
